import { Skeleton, TableCell, TableRow, cn } from '@parcel/ui';
import type { Table as ReactTable, RowData } from '@tanstack/react-table';
import type * as React from 'react';
import { DATA_TABLE_STICKY_HEADER_TOP_CLASS } from './data-table-infinite.js';

export interface PinnedColumn {
  id: string;
  left: number;
  width: number;
  last?: boolean;
}

export function getPinnedColumn(columns: PinnedColumn[], id: string) {
  return columns.find((column) => column.id === id);
}

export function getPinnedColumnStyle(
  columns: PinnedColumn[],
  id: string,
): React.CSSProperties | undefined {
  const column = getPinnedColumn(columns, id);
  if (!column) return undefined;

  return {
    left: column.left,
    minWidth: column.width,
    width: column.width,
    maxWidth: column.width,
  };
}

/**
 * Style for any table column: pinned columns keep their sticky width, and
 * non-pinned columns with an explicit ColumnDef `size` get width + minWidth so
 * they can't be squeezed below their size (squeezed nowrap text bleeds into
 * neighboring cells). No maxWidth — the table is w-full, so leftover space
 * stretches sized columns evenly; truncated cell content is what keeps a
 * column from growing past its size. Columns without a size stay auto.
 */
export function getColumnStyle(
  pinnedColumns: PinnedColumn[],
  column: { id: string; columnDef: { size?: number } },
): React.CSSProperties | undefined {
  const pinned = getPinnedColumnStyle(pinnedColumns, column.id);
  if (pinned) return pinned;

  const size = column.columnDef.size;
  if (size == null) return undefined;
  return { width: size, minWidth: size };
}

export function getPinnedColumnClassName(columns: PinnedColumn[], id: string) {
  const column = getPinnedColumn(columns, id);
  if (!column) return undefined;

  return cn(
    // The active-row tint must stay OPAQUE here: sticky cells cover content
    // that scrolls beneath them, and a translucent bg (bg-muted/60) lets it
    // show through. color-mix pre-blends muted@60% over the background so the
    // tint matches the row's data-[active]:bg-muted/60 without transparency.
    'sticky bg-background group-hover/row:bg-muted group-data-[state=selected]/row:bg-muted group-data-[active]/row:bg-[color-mix(in_srgb,hsl(var(--muted))_60%,hsl(var(--background)))]',
    // A collapsed table border scrolls away from a sticky cell, so pinned dividers are drawn on the cell.
    'border-r-0',
    column.last
      ? 'z-20 after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-border after:shadow-[4px_0_8px_hsl(var(--foreground)/0.08)]'
      : 'z-20 shadow-[inset_-1px_0_0_hsl(var(--border))]',
  );
}

/** Pinned header cells need the same top + higher z so body sticky cells don't cover them. */
export function getPinnedHeaderClassName(columns: PinnedColumn[], id: string) {
  const pinned = getPinnedColumnClassName(columns, id);
  if (!pinned) return undefined;
  return cn(pinned, DATA_TABLE_STICKY_HEADER_TOP_CLASS, 'z-30');
}

export function DataTableRoot({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-infinite-table-root
      className={cn('flex min-w-0 w-full flex-col', className)}
      {...props}
    />
  );
}

export function DataTableToolbar({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex min-w-0 flex-wrap items-center gap-2 border-b px-6 py-2.5', className)}
      {...props}
    />
  );
}

export function DataTableViewport({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        // Fixed layout keeps widths stable; non-pinned cells clip so wide content never spills into the next column.
        'min-w-0 w-full overflow-x-auto border-b [&_table]:table-fixed [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap [&_td:not(.sticky)]:overflow-hidden [&_td:not(.sticky)]:text-ellipsis [&_th:not(.sticky)]:overflow-hidden [&_th:not(.sticky)]:text-ellipsis',
        className,
      )}
      {...props}
    />
  );
}

// Stable keys so skeleton rows aren't keyed by array index.
const SKELETON_ROW_KEYS = Array.from({ length: 12 }, (_, i) => `skeleton-row-${i}`);

/**
 * Placeholder rows shown on the initial load of a data table (before any data
 * has arrived). Mirrors the real row/column layout — same height, same pinned
 * columns — so the table doesn't jump when data replaces it. Skips the select
 * and actions columns, which have no meaningful content to fake.
 */
export function DataTableSkeletonRows<TData extends RowData>({
  table,
  pinnedColumns,
  rows = 8,
  rowClassName = 'h-14',
}: {
  table: ReactTable<TData>;
  pinnedColumns: PinnedColumn[];
  rows?: number;
  rowClassName?: string;
}) {
  const columns = table.getVisibleLeafColumns();
  return (
    <>
      {SKELETON_ROW_KEYS.slice(0, rows).map((rowKey) => (
        <TableRow key={rowKey} className={cn(rowClassName, 'hover:bg-transparent')}>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              style={getColumnStyle(pinnedColumns, column)}
              className={cn(
                getPinnedColumnClassName(pinnedColumns, column.id),
                column.id === 'select' && 'p-0 text-center',
              )}
            >
              {column.id === 'select' || column.id === 'actions' ? null : (
                <Skeleton className="h-4 w-2/3" />
              )}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export { DataTableColumnHeader } from './data-table-column-header.js';
export { DataTableColumnsToggle } from './data-table-columns-toggle.js';
export {
  DATA_TABLE_MULTI_LINE_ROW_HEIGHT,
  DATA_TABLE_OVERSCAN,
  DATA_TABLE_PREFETCH_THRESHOLD,
  DATA_TABLE_ROW_HEIGHT,
  DATA_TABLE_STICKY_HEADER_TOP_CLASS,
  DataTableLoadStatus,
  formatLoadedStatus,
  shouldPrefetchNextPage,
  useInfiniteTableVirtualizer,
} from './data-table-infinite.js';
export { DATA_TABLE_DEFAULT_PAGE_SIZE, DataTablePagination } from './data-table-pagination.js';
